import { useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  Heading,
  Switch,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiTrash2 } from 'react-icons/fi'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useLobbyDayEvent } from '../../../hooks/useLobbyDay'
import {
  useRemoveTarget, useSetEventTargetAll, useSetMemberTarget, useTargetCoverage,
} from '../../../hooks/useLobbyRecruit'
import { ResponsiveTable, type ResponsiveColumn } from '../../shared'
import { StatTile } from '../../shared/viz'
import { AddTargetPicker } from './AddTargetPicker'
import { chamberDistrictLabel, coverageSummary } from './recruitCoverage'
import { LegislatorFormModal } from '../LegislatorFormModal'
import type { Chamber, TargetCoverageDto } from '../../../types/lobbyDay'

interface TargetsCoveragePanelProps {
  eventId: string
  onFindConstituents: (arg: { chamber: Chamber; districtValues: string[] }) => void
}

export function TargetsCoveragePanel({ eventId, onFindConstituents }: TargetsCoveragePanelProps) {
  const isMobile = useIsMobile()
  const { data: event } = useLobbyDayEvent(eventId)
  const { data: rows, isLoading } = useTargetCoverage(eventId)
  const removeTarget = useRemoveTarget(eventId)
  const setTargetAll = useSetEventTargetAll(eventId)
  const setMemberTarget = useSetMemberTarget(eventId)
  const [modalLegislatorId, setModalLegislatorId] = useState<string | null>(null)

  const targetAll = event?.targetAllLegislators ?? true
  const summary = useMemo(() => coverageSummary(rows ?? [], targetAll), [rows, targetAll])

  const handleRemove = async (row: TargetCoverageDto) => {
    try {
      await removeTarget.mutateAsync(row.targetId)
      toaster.create({ type: 'success', title: `${row.legislatorName} removed from this event` })
    } catch {
      toaster.create({ type: 'error', title: 'Failed to remove legislator' })
    }
  }

  const handleToggleTargetAll = async (checked: boolean) => {
    try {
      await setTargetAll.mutateAsync(checked)
    } catch {
      toaster.create({ type: 'error', title: 'Failed to update targeting' })
    }
  }

  const handleToggleMember = async (row: TargetCoverageDto, isTarget: boolean) => {
    try {
      await setMemberTarget.mutateAsync({ targetId: row.targetId, isTarget })
    } catch {
      toaster.create({ type: 'error', title: 'Failed to update target' })
    }
  }

  const columns: ResponsiveColumn<TargetCoverageDto>[] = [
    ...(!targetAll
      ? [{
          header: 'Target',
          mobileRole: 'badge' as const,
          cell: (row: TargetCoverageDto) => (
            <Checkbox.Root
              minH="44px"
              onCheckedChange={(e) => handleToggleMember(row, e.checked === true)}
              aria-label={`Target ${row.legislatorName}`}
              checked={row.isTarget}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
          ),
        }]
      : []),
    {
      header: 'Legislator',
      mobileRole: 'title',
      cell: (row) => (
        <Text as="button" textAlign="left"
              color="accent" cursor="pointer" _hover={{ textDecoration: 'underline' }}
              onClick={() => setModalLegislatorId(row.legislatorId)}
              {...({ type: 'button' } as Record<string, unknown>)}>
          {row.isPriority ? `★ ${row.legislatorName}` : row.legislatorName}
        </Text>
      ),
    },
    {
      header: 'District',
      mobileRole: 'meta',
      cell: (row) => chamberDistrictLabel(row.chamber, row.district),
    },
    {
      header: 'Constituent attendees',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (row) => (
        <Text
          color={!targetAll && !row.isTarget ? 'gray.400' : row.constituentAttendeeCount === 0 ? 'red.500' : undefined}
          fontWeight={row.constituentAttendeeCount === 0 ? 'semibold' : 'normal'}
        >
          {row.constituentAttendeeCount}
        </Text>
      ),
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (row) =>
        !targetAll && !row.isTarget ? (
          <Badge colorPalette="gray">Not targeted</Badge>
        ) : row.isGap ? (
          <Badge colorPalette="red">{row.isPriority ? 'Gap · priority' : 'Gap'}</Badge>
        ) : (
          <Badge colorPalette="green">Covered</Badge>
        ),
    },
    {
      header: '',
      mobileRole: 'badge',
      cell: (row) =>
        row.isGap ? (
          <Button size="sm" colorPalette="brand" minH={isMobile ? '44px' : undefined} onClick={() => onFindConstituents({ chamber: row.chamber, districtValues: row.matchedDistrictValues })}>
            Find constituents
          </Button>
        ) : (
          <Button size="sm" variant="ghost" minH={isMobile ? '44px' : undefined} onClick={() => onFindConstituents({ chamber: row.chamber, districtValues: row.matchedDistrictValues })}>
            Add more
          </Button>
        ),
    },
  ]

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack justify="space-between" mb={4} wrap="wrap" gap={4}>
        <Heading size="md">Event legislators &amp; coverage</Heading>
        <AddTargetPicker eventId={eventId} />
      </HStack>

      <HStack mb={4} gap={3}>
        <Switch.Root
          checked={targetAll}
          onCheckedChange={(e) => handleToggleTargetAll(e.checked === true)}
          disabled={setTargetAll.isPending}
        >
          <Switch.HiddenInput />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Root>
        <Text fontSize="sm">
          Target all legislators on this event
          {!targetAll && <Text as="span" color="gray.500"> — check the ones you're targeting below</Text>}
        </Text>
      </HStack>

      <HStack gap={6} mb={4} wrap="wrap">
        <StatTile label="Targets" value={summary.targets} />
        <StatTile label="Covered" value={<Text as="span" color="success.fg">{summary.covered}</Text>} />
        <StatTile label="Gaps" value={<Text as="span" color="danger.fg">{summary.gaps}</Text>} />
        <StatTile label="Priority gaps" value={<Text as="span" color="danger.fg">{summary.priorityGaps}</Text>} />
      </HStack>

      <ResponsiveTable
        bare
        columns={columns}
        rows={rows ?? []}
        keyFn={(row) => row.targetId}
        isLoading={isLoading}
        rowActions={[{
          label: 'Remove',
          icon: FiTrash2,
          color: 'red.500',
          onClick: handleRemove,
          isLoading: (row) => removeTarget.isPending && removeTarget.variables === row.targetId,
        }]}
        emptyMessage="No legislators on this event yet — add the legislators you want to lobby."
      />
      <LegislatorFormModal
        isOpen={!!modalLegislatorId}
        legislatorId={modalLegislatorId ?? undefined}
        onClose={() => setModalLegislatorId(null)}
      />
    </Box>
  );
}
